import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { addRippleEffect } from '../functions/classes';
import { auth, db } from '../firebase';
import logo from '../assets/logo.png';
import logoIcon from '../assets/logo-icon.png';
import Pricing from './pricing';
import '../webapp.css'; 
import { PiCrown, PiShieldCheck, PiX } from 'react-icons/pi';
import { BsPersonVideo3 } from "react-icons/bs";
import { TbFolderOpen } from "react-icons/tb";
import { GrSend } from "react-icons/gr";
import Profile from './profile';
import { AlertStack } from '../functions/ui';
import PremiumPopup from './premiumpopup';

const Dashboard = () => {
  const [showPlan, setShowPlan] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    let unsubUser = null;
    let lastUid = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Only re-subscribe if user changes
        if (lastUid !== user.uid && unsubUser) {
          unsubUser();
        }
        lastUid = user.uid;
        const userRef = doc(db, 'companyUsers', user.uid);
        unsubUser = onSnapshot(
          userRef,
          (userSnap) => {
            if (userSnap.exists()) {
              const data = userSnap.data();
              setUserStatus({ premium: !!data.premium, freeShift: data.freeShift || 0, loaded: true });
              setError(null);
              // Hide modal if user is now premium
              if (!!data.premium) {
                setShowLimitModal(false);
                setShowPlan(false);
              } else if (!data.premium && data.freeShift === 10) {
                setShowLimitModal(true);
              }
            } else {
              setUserStatus(null);
              setError('No user data found');
            }
            setLoading(false);
          },
          (err) => {
            setError('Failed to load user data');
            setLoading(false);
          }
        );
      } else {
        if (unsubUser) unsubUser();
        setUserStatus(null);
        setLoading(false);
      }
    });
    return () => {
      if (unsubUser) unsubUser();
      unsubAuth();
    };
  }, []);

  useEffect(() => {
    // Show welcome alert if redirected from Stripe success
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setAlerts(prev => [
        ...prev,
        { id: Date.now() + Math.random(), message: 'Welcome to the family!', type: 'success' }
      ]);
      // Optionally remove the param from the URL after showing
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Loading spinner or error message
  if (loading) {
    return <div className="spinner" style={{margin: "2rem auto"}} />;
  }
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <>
      <div className="dash-main-box">
        <nav className="sidebar">
          <img src={logo} alt="Logo" className="sidebar-logo"/>
          <img src={logoIcon} alt="Logo Icon" className="sidebar-logo-icon" />
          <ul>
            <li>
              <NavLink 
                to="/dashboard/shiftcall" 
                onClick={addRippleEffect}
              >
                <GrSend className="sidebar-icon"/> 
                <span>Shift Call</span>
              </NavLink>
            </li> 
            <li>
              <NavLink 
                to="/dashboard/shift-info"
                onClick={addRippleEffect}
              >
                <TbFolderOpen className="sidebar-icon"/> 
                <span>Shift Details</span>
              </NavLink>
            </li>
            <li>
              <NavLink 
                to="/dashboard/staff"
                onClick={addRippleEffect}
              >
                <BsPersonVideo3 className="sidebar-icon"/> 
                <span>Staff List</span>
              </NavLink>
            </li>
            <div className ='spacer'/>
            {userStatus && userStatus.loaded && !userStatus.premium && (
              <li>
                <button onClick={e => {setShowPlan(true); }}>
                  <PiCrown className="sidebar-icon get-premium-icon" />
                  <span>Get Premium</span>
                </button>
              </li>
            )}
            {/* Always show Get Premium button when PremiumLimitModal is showing */}
            {showLimitModal && (
              <li>
                <button onClick={e => {setShowPlan(true); }}>
                  <PiCrown className="sidebar-icon get-premium-icon" />
                  <span>Get Premium</span>
                </button>
              </li>
            )}
            {userStatus && userStatus.loaded && userStatus.premium && (
              <li>
                <PiShieldCheck className="sidebar-icon get-premium-icon" />
                <span>Premium Badge</span>
              </li>
            )}
          </ul>
        </nav>
        <div className="dashboard-content">
          <Profile/>
          <Outlet /> 
        </div>
      </div>
      {/* PremiumLimitModal should be rendered first, then Pricing modal so Pricing is on top */}
      {showLimitModal && (
        <PremiumPopup onGetPremium={() => { setShowPlan(true); }} />
      )}
      {showPlan && (
        <div className="modal-overlay pricing-modal-overlay" onClick={() => setShowPlan(false)}>
          <div className="plan-modal-wrapper" onClick={e => e.stopPropagation()}>
            <button className="plan-modal-close" onClick={() => setShowPlan(false)}>
              <PiX/>
            </button>
            <Pricing />
          </div>
        </div>
      )}
      <AlertStack alerts={alerts} onAlertClose={id => setAlerts(alerts => alerts.filter(a => a.id !== id))} />
    </>
  );
};

export default Dashboard;